/**
 * Client Dashboard — redesigned with ICSC BOSS design system.
 * Shows KPIs, certification journey, and quick-nav cards.
 * Does NOT embed full pages as tabs (sidebar handles navigation).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, KPIGrid, Card, Btn,
  EmptyState, StatusBadge,
  colors, spacing, typography, radius, shadows,
} from '../design-system';

// ── Journey stages ─────────────────────────────────────────────────────────────

const STAGES = [
  { key: 'stage_1_audit', label: 'Stage 1 Audit', desc: 'Document review',    color: '#fd7e14' },
  { key: 'stage_2_audit', label: 'Stage 2 Audit', desc: 'On-site assessment', color: '#6f42c1' },
  { key: 'certification', label: 'Certified',      desc: 'ISO cert issued',    color: '#059669' },
];

// ── NavCard ────────────────────────────────────────────────────────────────────

function NavCard({ icon, label, description, to, badge, accentColor }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const accent = accentColor || colors.primary;
  return (
    <div
      onClick={() => navigate(to)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: colors.surface,
        border: `1px solid ${hovered ? accent : colors.border}`,
        borderRadius: radius.lg,
        padding: `${spacing.md} ${spacing.lg}`,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        transition: 'all 150ms ease',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? shadows.md : shadows.cardShadow,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: accent }} />
      <span style={{ fontSize: '20px', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, lineHeight: 1.2 }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {description}
          </div>
        )}
      </div>
      {badge != null && badge > 0 && (
        <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: radius.pill, fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.bold, padding: '2px 8px', flexShrink: 0 }}>
          {badge}
        </span>
      )}
      <span style={{ color: hovered ? accent : colors.textMuted, fontSize: '14px', flexShrink: 0 }}>›</span>
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md }}>
      <span style={{ fontSize: '18px' }}>{icon}</span>
      <div>
        <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.bold, color: colors.textPrimary }}>{title}</div>
        {subtitle && <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>{subtitle}</div>}
      </div>
    </div>
  );
}

// ── Certification Journey ──────────────────────────────────────────────────────

function CertificationJourney({ records }) {
  const byStandard = {};
  records.forEach(r => {
    if (!byStandard[r.iso_standard]) byStandard[r.iso_standard] = {};
    const existing = byStandard[r.iso_standard][r.record_type];
    const rDate = new Date(r.audit_date || r.issue_date);
    if (!existing || rDate > new Date(existing.audit_date || existing.issue_date)) {
      byStandard[r.iso_standard][r.record_type] = r;
    }
  });

  const standards = Object.keys(byStandard);
  if (standards.length === 0) {
    return (
      <EmptyState
        icon="🏆"
        title="No certification activity yet"
        subtitle="Your certification body will log audit and certification records here."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      {standards.map(std => {
        const stages = byStandard[std];
        const certified = !!stages['certification'];
        return (
          <div key={std} style={{
            background: colors.surface,
            borderRadius: radius.lg,
            padding: spacing.lg,
            border: certified ? `2px solid ${colors.success}` : `1px solid ${colors.border}`,
            boxShadow: shadows.cardShadow,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
              <span style={{ fontWeight: typography.fontWeights.bold, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>{std}</span>
              {certified && (
                <span style={{ fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.bold, color: '#065f46', background: '#d1fae5', padding: '3px 10px', borderRadius: radius.pill }}>
                  ✓ CERTIFIED
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              {STAGES.map((stage, idx) => {
                const rec = stages[stage.key];
                const done = !!rec;
                const isLast = idx === STAGES.length - 1;
                const dateStr = rec ? new Date(rec.audit_date || rec.issue_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
                return (
                  <React.Fragment key={stage.key}>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%', margin: '0 auto',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: done ? 18 : 15, fontWeight: typography.fontWeights.bold,
                        background: done ? stage.color : colors.background,
                        color: done ? 'white' : colors.textMuted,
                        border: `2px solid ${done ? stage.color : colors.border}`,
                      }}>
                        {done ? (stage.key === 'certification' ? '🏆' : '✓') : idx + 1}
                      </div>
                      <div style={{ marginTop: spacing.xs, fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold, color: done ? colors.textPrimary : colors.textMuted }}>
                        {stage.label}
                      </div>
                      <div style={{ fontSize: '11px', color: colors.textMuted }}>{stage.desc}</div>
                      {done ? (
                        <div style={{ fontSize: '11px', color: stage.color, fontWeight: typography.fontWeights.semibold, marginTop: '4px' }}>
                          {dateStr}
                          {rec.auditor_name && <div style={{ color: colors.textMuted, fontWeight: typography.fontWeights.normal }}>by {rec.auditor_name}</div>}
                          {stage.key === 'certification' && rec.certificate_number && (
                            <div style={{ fontFamily: 'monospace', fontSize: '10px', color: colors.textMuted, marginTop: '2px' }}>{rec.certificate_number}</div>
                          )}
                          {stage.key === 'certification' && rec.expiry_date && (
                            <div style={{ fontSize: '10px', color: colors.textMuted }}>
                              Expires {new Date(rec.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: colors.border, marginTop: '4px' }}>Pending</div>
                      )}
                    </div>
                    {!isLast && (
                      <div style={{ flex: 0, width: 40, height: 2, marginTop: 21, background: stages[STAGES[idx + 1]?.key] ? stage.color : colors.border, alignSelf: 'flex-start' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ClientDashboard() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [certificates, setCertificates] = useState([]);
  const [feedback, setFeedback]         = useState([]);
  const [standards, setStandards]       = useState([]);
  const [visibility, setVisibility]     = useState(null);
  const [tasks, setTasks]               = useState([]);
  const [invoices, setInvoices]         = useState([]);
  const [payInfo, setPayInfo]           = useState(null);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    Promise.allSettled([
      axiosClient.get('/certifications/certifications/'),
      axiosClient.get('/feedback'),
      axiosClient.get('/pdf/standards'),
      axiosClient.get('/visibility/contract'),
      axiosClient.get('/client/tasks'),
      axiosClient.get('/client/invoices'),
    ]).then(([certsR, feedR, stdsR, visR, tasksR, invR]) => {
      const get = r => r.status === 'fulfilled' ? r.value.data : null;
      setCertificates(Array.isArray(get(certsR)) ? get(certsR) : []);
      setFeedback(Array.isArray(get(feedR)) ? get(feedR) : []);
      setStandards(Array.isArray(get(stdsR)) ? get(stdsR) : []);
      setVisibility(get(visR) || {
        show_iso_applications: true, show_certificates: true,
        show_assigned_courses: true, show_evidence_review: true,
        show_competency_dashboard: true, show_tickets: true,
      });
      setTasks(Array.isArray(get(tasksR)) ? get(tasksR) : []);
      setInvoices(Array.isArray(get(invR)) ? get(invR) : []);
    }).finally(() => setLoading(false));
  }, []);

  const openTasks = tasks.filter(t => !t.completed);
  const outstandingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'overdue');

  const handlePayInvoice = async (invoiceId) => {
    setPayInfo(null);
    try {
      const { data } = await axiosClient.post(`/client/invoices/${invoiceId}/pay`, {});
      if (data.status === 'redirect' && data.paymentUrl) {
        window.open(data.paymentUrl, '_blank', 'noopener,noreferrer');
      } else {
        setPayInfo({ invoiceId, ...data });
      }
    } catch (e) {
      setPayInfo({ invoiceId, status: 'error', message: 'Could not start payment. Please try again or contact ICSC billing.' });
    }
  };

  const activeCerts  = certificates.filter(c => c.status === 'active' && c.record_type === 'certification');
  const latestFeedback = feedback[0];

  const kpis = [
    { label: 'ISO Certificates',     value: loading ? '…' : activeCerts.length,    icon: '🏆', color: colors.success },
    { label: 'Total Records',        value: loading ? '…' : certificates.length,    icon: '📄', color: colors.primary },
    { label: 'Audit Feedbacks',      value: loading ? '…' : feedback.length,        icon: '📋', color: '#7c3aed' },
    { label: 'ISO Standards',        value: loading ? '…' : standards.length,       icon: '📚', color: colors.info },
  ];

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${user.full_name || user.username || 'Client'}`}
        subtitle={user.company_name ? `${user.company_name} — Client Portal` : 'Client Portal'}
      />

      {/* KPI Strip */}
      <KPIGrid kpis={kpis} columns={4} />

      {/* Quick Navigation */}
      <div style={{ marginBottom: spacing.xl }}>
        <SectionHeader icon="⚡" title="Quick Access" subtitle="Jump to the tools you use most" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: spacing.md }}>
          {visibility?.show_certificates !== false && (
            <NavCard icon="📜" label="My Certificates"       description="View and download your ISO certificates"       to="/certificates"           accentColor={colors.success} />
          )}
          {visibility?.show_iso_applications !== false && (
            <NavCard icon="📄" label="ISO Documents"         description="Standards library and document templates"      to="/documents"              accentColor={colors.primary} />
          )}
          {visibility?.show_evidence_review !== false && (
            <NavCard icon="📁" label="Evidence"              description="Upload and manage evidence files"              to="/evidence"               accentColor="#7c3aed" />
          )}
          {visibility?.show_assigned_courses !== false && (
            <NavCard icon="📖" label="My Courses"            description="Learning modules assigned to you"             to="/my-courses"             accentColor={colors.info} />
          )}
          {visibility?.show_competency_dashboard !== false && (
            <NavCard icon="🎯" label="Competency (PCAF)"     description="Professional competency framework courses"    to="/competency/my-courses"  accentColor="#059669" />
          )}
          {visibility?.show_tickets !== false && (
            <NavCard icon="🎫" label="Support Tickets"       description="Raise and track support requests"            to="/tickets"                accentColor={colors.warning} />
          )}
          <NavCard   icon="👤" label="My Profile"            description="Manage your account and preferences"         to="/profile"                accentColor={colors.textMuted} />
        </div>
      </div>

      {/* Open Tasks strip */}
      {!loading && openTasks.length > 0 && (
        <div style={{ marginBottom: spacing.xl }}>
          <SectionHeader icon="✅" title="Action Items" subtitle={`${openTasks.length} task${openTasks.length === 1 ? '' : 's'} awaiting action`} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {openTasks.slice(0, 5).map(t => {
              const urgencyColor = t.priority === 'urgent' ? colors.danger : t.priority === 'important' ? colors.warning : colors.textMuted;
              return (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: spacing.md,
                  background: colors.surface, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${urgencyColor}`,
                  borderRadius: radius.md, padding: `${spacing.sm} ${spacing.md}`,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>{t.title}</div>
                    {t.dueDate && (
                      <div style={{ fontSize: '11px', color: colors.textMuted }}>Due {new Date(t.dueDate).toLocaleDateString('en-GB')}</div>
                    )}
                  </div>
                  <StatusBadge status={t.priority} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main content — 2 columns on wide screens */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: spacing.xl, alignItems: 'start' }}>

        {/* Certification Journey */}
        <div>
          <SectionHeader icon="🏆" title="My Certification Journey" subtitle="ISO certification progress and records" />
          <CertificationJourney records={certificates} />
        </div>

        {/* Right column: feedback + billing */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
        <div>
          <SectionHeader icon="📋" title="Latest Audit Feedback" subtitle="Most recent auditor comments" />
          {latestFeedback ? (
            <Card>
              <div style={{ marginBottom: spacing.md }}>
                <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginBottom: 2 }}>
                  {new Date(latestFeedback.audit_date).toLocaleDateString('en-GB')} · {latestFeedback.audit_type}
                </div>
                <div style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary }}>
                  Auditor: {latestFeedback.auditor_name}
                </div>
              </div>

              {latestFeedback.strengths?.length > 0 && (
                <div style={{ marginBottom: spacing.md }}>
                  <div style={{ fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold, color: colors.success, marginBottom: spacing.xs, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Strengths
                  </div>
                  <ul style={{ margin: 0, paddingLeft: spacing.lg }}>
                    {latestFeedback.strengths.map((s, i) => (
                      <li key={i} style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, marginBottom: 4 }}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}

              {latestFeedback.improvements?.length > 0 && (
                <div style={{ marginBottom: spacing.md }}>
                  <div style={{ fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold, color: colors.danger, marginBottom: spacing.xs, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Areas for Improvement
                  </div>
                  <ul style={{ margin: 0, paddingLeft: spacing.lg }}>
                    {latestFeedback.improvements.map((s, i) => (
                      <li key={i} style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, marginBottom: 4 }}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}

              {latestFeedback.recommendation && (
                <div style={{ background: colors.background, borderRadius: radius.md, padding: spacing.md, fontSize: typography.fontSizes.sm, color: colors.textSecondary }}>
                  <strong>Recommendation:</strong> {latestFeedback.recommendation}
                </div>
              )}
            </Card>
          ) : (
            <EmptyState icon="📋" title="No feedback yet" subtitle="Audit feedback will appear here after your first assessment." />
          )}
        </div>

        {/* Billing panel */}
        {outstandingInvoices.length > 0 && (
          <div>
            <SectionHeader icon="💳" title="Billing" subtitle={`${outstandingInvoices.length} invoice${outstandingInvoices.length === 1 ? '' : 's'} outstanding`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              {outstandingInvoices.map(inv => (
                <Card key={inv.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: spacing.sm }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>{inv.invoiceNumber}</div>
                      <div style={{ fontSize: '11px', color: colors.textMuted }}>{inv.description} · Due {new Date(inv.dueDate).toLocaleDateString('en-GB')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexShrink: 0 }}>
                      <span style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.bold, color: colors.textPrimary }}>
                        {inv.currency || 'GBP'} {Number(inv.amount).toFixed(2)}
                      </span>
                      <Btn size="sm" onClick={() => handlePayInvoice(inv.id)}>Pay</Btn>
                    </div>
                  </div>
                  {payInfo?.invoiceId === inv.id && (
                    <div style={{ marginTop: spacing.sm, padding: spacing.sm, background: colors.background, borderRadius: radius.md, fontSize: typography.fontSizes.xs, color: colors.textSecondary }}>
                      {payInfo.message}
                      {[
                        ['Bank', payInfo.bank_name],
                        ['Account Name', payInfo.account_name],
                        ['Sort Code', payInfo.sort_code],
                        ['Account Number', payInfo.account_number],
                        ['IBAN', payInfo.iban],
                        ['BIC/Swift', payInfo.bic_swift],
                        ['Bank Address', payInfo.bank_address],
                        ['Reference', payInfo.reference],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label} style={{ marginTop: 4 }}>{label}: <strong>{value}</strong></div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
